import React, { useEffect } from 'react';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import QueryString from 'qs';
import moment from 'moment';
import { toast } from 'react-toastify';
import PageWithCard from '../../components/infrastructure/PageWithCard';
import PrimaryButton from '../../components/infrastructure/Buttons/PrimaryButton';
import DangerButton from '../../components/infrastructure/Buttons/DangerButton';
import FormikInputGroup from '../../components/formik/FormikInputGroup';
import FormikAsyncSelect from '../../components/formik/FormikAsyncSelect';
import FormikSelectGroup from '../../components/formik/FormikSelectGroup';
import FormikDirectImageUpload from '../../components/formik/FormikDirectImageUpload';
import FormikDirectFileUpload from '../../components/formik/FormikDirectFileUpload';
import FormikInputDateGroup from '../../components/formik/FormikInputDateGroup';
import FormikTextareaGroup from '../../components/formik/FormikTextareaGroup';
import { authAxiosInstance } from '../../utils/axiosConfig';
import { getAuth } from '../../app/reducers/Auth/authSlice';
import {
    fetchEmployees,
    getEmployees,
} from '../../app/reducers/Users/EmployeeSlice';
import {
    MATERIAL_CONDITION_OPTIONS,
    SHIFTS_OPTIONS,
    MACHINE_WARRANTY_OPTIONS,
    YES_NO_OPTION,
    COMPETITOR_APPROACH,
} from '../../utils/dropdownOptions';
import { generateEmployeeOptions } from '../../utils/Utils';

const AddVisitForm = () => {
    const dispatch = useDispatch();
    const { user } = useSelector(getAuth);
    const { employees } = useSelector(getEmployees);

    useEffect(() => {
        dispatch(fetchEmployees({ limit: 500 }));
    }, []);

    const formik = useFormik({
        initialValues: {
            company: '',
            date: moment().valueOf(),
            createdBy: user?._id || '',
            creatorName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
            materialCondition: '',
            isOtherMaterialCondition: false,
            shifts: '',
            isOtherShifts: false,
            competitorInkApproach: '',
            whichCompetitorInk: '',
            competitorMachineApproach: '',
            whichCompetitorMachine: '',
            resolution: '',
            photos: [],
            videos: [],
        },
        validationSchema: Yup.object({
            date:Yup.string().required('Date is required'),
            company: Yup.string().required('Company is required'),
            createdBy: Yup.string().required('Created By is required'),
            shifts: Yup.string().required('Shifts is required'),
            materialCondition: Yup.string().required(
                'Material Condition is required'
            ),
            competitorInkApproach: Yup.string().required(
                'Competitor Ink Approach is required'
            ),

            competitorMachineApproach: Yup.string().required(
                'Competitor Machine Approach is required'
            ),
            whichCompetitorInk: Yup.string().when(
                'competitorInkApproach',
                {
                    is: 'yes',
                    then: (schema) =>
                        schema.required('Which Competitor Ink is required'),
                    otherwise: (schema) => schema.notRequired(),
                }
            ),
            whichCompetitorMachine: Yup.string().when(
                'competitorMachineApproach',
                {
                    is: 'yes',
                    then: (schema) =>
                        schema.required('Which Competitor Machine is required'),
                    otherwise: (schema) => schema.notRequired(),
                }
            ),
        }),
        onSubmit: async (values) => {
            try {
                const {
                    isOtherMaterialCondition,
                    isOtherShifts,
                    isOtherMachineWarranty,
                    ...payload
                } = values;
                await authAxiosInstance.post('/visitForm', payload);
                toast.success('Visit form created');
                formik.resetForm();
            } catch (error) {
                if (error?.response?.data?.error?.message) {
                    toast.error(error.response.data.error.message);
                } else {
                    toast.error('Failed to create visit form');
                }
                console.log(error);
            }
        },
    });

    return (
        <PageWithCard heading="Add Visit Form">
            <form
                onSubmit={formik.handleSubmit}
                className="flex flex-col gap-4"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormikInputDateGroup
                        label="Date"
                        name="date"
                        formik={formik}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="dd/MM/yyyy HH:mm"
                        required
                    />

                    <FormikSelectGroup
                        label="Created By"
                        name="createdBy"
                        formik={formik}
                        options={generateEmployeeOptions(employees?.docs)}
                        required
                        isDisabled={!['sup_admin', 'admin', 'local_admin'].includes(user?.role)}
                        onChange={(selectedOption) => {
                            formik.setFieldValue('createdBy', selectedOption.value);
                            formik.setFieldValue('creatorName', selectedOption.label);
                        }}
                    />
                </div>

                <FormikAsyncSelect
                    label="Select Company"
                    name="company"
                    formik={formik}
                    getOptions={async (value) => {
                        try {
                            const payload = {
                                search: value,
                                approvedStatus: 'approved',
                            };

                            const string = QueryString.stringify(payload);

                            const response = await authAxiosInstance.get(
                                `/company?${string}`
                            );

                            return (
                                response?.data?.data?.docs?.map((ele) => ({
                                    label: ele.name,
                                    value: ele._id,
                                })) || []
                            );
                        } catch (error) {
                            console.log(error);
                            return [];
                        }
                    }}
                    onChange={(selectedOption) => {
                        formik.setFieldValue('company', selectedOption.value);
                    }}
                    required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {formik.values.isOtherMaterialCondition ? (
                        <div>
                            <FormikInputGroup
                                name="materialCondition"
                                label="Material Condition"
                                formik={formik}
                                required={true}
                            />

                            <p
                                className="text-blue-600 text-sm cursor-pointer underline mt-1"
                                onClick={() => {
                                    formik.setFieldValue('materialCondition', '');
                                    formik.setFieldValue(
                                        'isOtherMaterialCondition',
                                        false
                                    );
                                }}
                            >
                                Select from list
                            </p>
                        </div>
                    ) : (
                        <FormikSelectGroup
                            label="Material Condition"
                            name="materialCondition"
                            formik={formik}
                            required
                            options={MATERIAL_CONDITION_OPTIONS}
                            onChange={(selectedOption) => {
                                if (selectedOption?.value === 'other') {
                                    formik.setFieldValue('materialCondition', '');
                                    formik.setFieldValue(
                                        'isOtherMaterialCondition',
                                        true
                                    );
                                } else {
                                    formik.setFieldValue(
                                        'materialCondition',
                                        selectedOption?.value
                                    );

                                    formik.setFieldValue(
                                        'isOtherMaterialCondition',
                                        false
                                    );
                                }
                            }}
                        />
                    )}

                    {formik.values.isOtherShifts ? (
                        <div>
                            <FormikInputGroup
                                name="shifts"
                                label="Shifts"
                                formik={formik}
                                required
                            />

                            <p
                                className="text-blue-600 text-sm cursor-pointer underline mt-1"
                                onClick={() => {
                                    formik.setFieldValue('shifts', '');
                                    formik.setFieldValue('isOtherShifts', false);
                                }}
                            >
                                Select from list
                            </p>
                        </div>
                    ) : (
                        <FormikSelectGroup
                            label="Shifts"
                            name="shifts"
                            formik={formik}
                            options={SHIFTS_OPTIONS}
                            onChange={(selectedOption) => {
                                if (selectedOption?.value === 'other') {
                                    formik.setFieldValue('shifts', '');
                                    formik.setFieldValue('isOtherShifts', true);
                                } else {
                                    formik.setFieldValue(
                                        'shifts',
                                        selectedOption?.value
                                    );

                                    formik.setFieldValue('isOtherShifts', false);
                                }
                            }}
                            required
                        />
                    )}
                </div>

                {/* Competitor Ink */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <FormikSelectGroup
                        label="Competitor Ink Approach"
                        name="competitorInkApproach"
                        formik={formik}
                        options={COMPETITOR_APPROACH}
                        required
                    />

                    {formik?.values?.competitorInkApproach === 'yes' && (
                        <FormikInputGroup
                            label="Which Competitor Ink"
                            name="whichCompetitorInk"
                            formik={formik}
                            required
                        />
                    )}
                </div>

                {/* Competitor Machine */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <FormikSelectGroup
                        label="Competitor Machine Approach"
                        name="competitorMachineApproach"
                        formik={formik}
                        options={COMPETITOR_APPROACH}
                        required
                    />

                    {formik?.values?.competitorMachineApproach === 'yes' && (
                        <FormikInputGroup
                            label="Which Competitor Machine"
                            name="whichCompetitorMachine"
                            formik={formik}
                            required
                        />
                    )}
                </div>

                <FormikTextareaGroup
                    label="Remark"
                    name="resolution"
                    formik={formik}
                />

                {/* Photos */}
                <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                        Photos
                    </h3>

                    <FormikProvider value={formik}>
                        <FieldArray
                            name="photos"
                            render={(arrayHelpers) => {
                                return (
                                    <div className="flex flex-col gap-3">

                                        {formik.values.photos.map((ele, i) => (
                                            <div
                                                className="flex items-center gap-3 bg-white p-3 rounded border"
                                                key={i}
                                            >
                                                <div className="flex-1">
                                                    <FormikDirectImageUpload
                                                        label={`Photo ${i + 1}`}
                                                        formik={formik}
                                                        name={`photos.${i}`}
                                                        randomize
                                                        location={'iml_group/visitForm/'}
                                                    />
                                                </div>

                                                <DangerButton
                                                    onClick={() => {
                                                        arrayHelpers.remove(i);
                                                    }}
                                                >
                                                    Remove
                                                </DangerButton>
                                            </div>
                                        ))}

                                        <div>
                                            <PrimaryButton
                                                onClick={() => {
                                                    arrayHelpers.push('');
                                                }}
                                            >
                                                Add Photo
                                            </PrimaryButton>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </FormikProvider>
                </div>

                {/* Videos */}
                <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                        Videos
                    </h3>

                    <FormikProvider value={formik}>
                        <FieldArray
                            name="videos"
                            render={(arrayHelpers) => {
                                return (
                                    <div className="flex flex-col gap-3">

                                        {formik.values.videos.map((ele, i) => (
                                            <div
                                                className="flex items-center gap-3 bg-white p-3 rounded border"
                                                key={i}
                                            >
                                                <div className="flex-1">
                                                    <FormikDirectFileUpload
                                                        label={`Video ${i + 1}`}
                                                        formik={formik}
                                                        name={`videos.${i}`}
                                                        randomize
                                                        location={'iml_group/visitForm/'}
                                                    />
                                                </div>

                                                <DangerButton
                                                    onClick={() => {
                                                        arrayHelpers.remove(i);
                                                    }}
                                                >
                                                    Remove
                                                </DangerButton>
                                            </div>
                                        ))}

                                        <div>
                                            <PrimaryButton
                                                onClick={() => {
                                                    arrayHelpers.push('');
                                                }}
                                            >
                                                Add Video
                                            </PrimaryButton>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </FormikProvider>
                </div>

                <div className="pt-2">
                    <PrimaryButton type="submit">
                        Submit
                    </PrimaryButton>
                </div>
            </form>
        </PageWithCard>
    );
};

export default AddVisitForm;
